import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Play, RotateCcw, CheckCircle, LoaderCircle } from 'lucide-react'
import { Mutation } from '../../../../api'
import { useModuleActions } from '../../../../hooks/useMyPermissions'
import AiExtractedItemsTable, { type RowCorrection } from './AiExtractedItemsTable'

interface Props {
  tender: any
  jobs: any[]
  refreshKey?: number
  onJobStarted: (jobId: string) => void
  onCorrectionsSubmit?: (corrections: RowCorrection[]) => void
  reExtracting?: boolean
  projectId?: string
}

const AiTenderTab: React.FC<Props> = ({ tender, jobs, refreshKey = 0, onJobStarted, onCorrectionsSubmit, reExtracting, projectId }) => {
  const { t } = useTranslation()
  const { canCreate } = useModuleActions('AI_EVALUATION', projectId)

  const [startTenderExtraction, { loading: extracting }] = useMutation(
    Mutation.START_AI_TENDER_EXTRACTION,
  )

  const tenderExtractionJob = jobs.find(
    (j: any) => j.type === 'TENDER_EXTRACTION' && j.status === 'COMPLETED',
  )

  const handleStartExtraction = async () => {
    try {
      const { data } = await startTenderExtraction({
        variables: { tenderId: tender.id },
      })
      if (data?.startAiTenderExtraction?.id) {
        onJobStarted(data.startAiTenderExtraction.id)
      }
    } catch (err) {
      console.error('Failed to start tender extraction:', err)
    }
  }

  return (
    <>
      {/* Extraction action bar */}
      <Card>
        <CardContent className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">{t('ai_extract_tender_items')}</span>
            {tenderExtractionJob && (
              <Badge variant="success" className="font-medium whitespace-nowrap">
                <CheckCircle className="h-3 w-3 mr-1" />
                {t('ai_completed')}
              </Badge>
            )}
          </div>
          {canCreate && (
            <Button
              onClick={handleStartExtraction}
              disabled={extracting}
              size="sm"
            >
              {extracting ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : tenderExtractionJob ? (
                <RotateCcw className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              {tenderExtractionJob
                ? t('ai_re_extract_tender')
                : t('ai_start_extraction')}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Extracted items table */}
      {tenderExtractionJob && (
        <AiExtractedItemsTable
          key={`tender-${refreshKey}`}
          tenderId={tender.id}
          onCorrectionsSubmit={onCorrectionsSubmit}
          reExtracting={reExtracting}
          projectId={projectId}
        />
      )}

      {/* Empty state when no extraction yet */}
      {!tenderExtractionJob && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <p className="text-sm">{t('ai_extract_tender_items_desc')}</p>
          </CardContent>
        </Card>
      )}
    </>
  )
}

export default AiTenderTab
